// features/tutorials/components/ProgressOverview.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Clock, Zap, Target } from 'lucide-react';
import type { LearningProgress, TutorialModule } from '../types';

interface ProgressOverviewProps {
    progress: LearningProgress;
    modules: TutorialModule[];
}

export const ProgressOverview: React.FC<ProgressOverviewProps> = ({ progress, modules }) => {
    const totalLessons = modules.reduce((acc: number, m: TutorialModule) => acc + m.lessons.length, 0);
    const completedLessons = progress.completedLessons?.length || 0;
    const totalTimeSpent = Math.floor((progress.totalTimeSpent || 0) / 60);
    const streakDays = progress.streakDays || 0;
    const completionPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

    const stats = [
        {
            icon: Trophy,
            label: 'Lessons Completed',
            value: `${completedLessons}/${totalLessons}`,
            color: 'from-amber-400 to-orange-500',
            delay: 0.1,
            gradient: 'bg-gradient-to-br from-amber-400 to-orange-500'
        },
        {
            icon: Clock,
            label: 'Time Spent',
            value: `${totalTimeSpent}m`,
            color: 'from-blue-400 to-indigo-500',
            delay: 0.2,
            gradient: 'bg-gradient-to-br from-blue-400 to-indigo-500'
        },
        {
            icon: Zap,
            label: 'Day Streak',
            value: `${streakDays}d`,
            color: 'from-purple-400 to-violet-500',
            delay: 0.3,
            gradient: 'bg-gradient-to-br from-purple-400 to-violet-500'
        },
        {
            icon: Target,
            label: 'Completion',
            value: `${completionPercent}%`,
            color: 'from-emerald-400 to-green-500',
            delay: 0.4,
            gradient: 'bg-gradient-to-br from-emerald-400 to-green-500'
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
        >
            {stats.map((stat) => (
                <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: stat.delay, type: 'spring', stiffness: 200 }}
                    whileHover={{ y: -4, scale: 1.05 }}
                    className="relative group"
                >
                    <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl blur-xl"
                        style={{ backgroundImage: `linear-gradient(to right, var(--tw-gradient-stops))` }}
                    />
                    <div className="relative bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 hover:shadow-xl transition-all duration-300">
                        <div className={`w-12 h-12 rounded-xl ${stat.gradient} flex items-center justify-center mb-3 shadow-lg`}>
                            <stat.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
                            {stat.value}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                            {stat.label}
                        </div>
                    </div>
                </motion.div>
            ))}
        </motion.div>
    );
};